import { useMemo } from "react";

import buildGroupContext from "../../utils/ratings/buildGroupContext";

/**
 * Derives a stable GroupContext from the enriched group array produced by
 * getGroupsRatings. Re-computes only when inputs change.
 *
 * Subject-dropdown filtering for shared-only teachers lives in useSubjectSelection.
 *
 * @param {Object}      params
 * @param {Array|null}  params.selectedGroupContent
 *   Enriched group array from useGroupData (post getGroupsRatings enrichment).
 *   Null/empty = no groups loaded yet.
 * @param {Object|null} params.effectiveEpr
 *   The teacher's own EPR. Null for shared-only teachers.
 * @param {Object|null} params.selectedTemplate
 *   The currently selected subject template. Null = no subject selected yet.
 *
 * @returns {{
 *   groupContext: GroupContext | null
 * }}
 * groupContext is null when selectedGroupContent is empty/null.
 */
const useGroupContext = ({ selectedGroupContent, effectiveEpr, selectedTemplate }) => {
    const groupContext = useMemo(() => {
        if (!selectedGroupContent?.length || !selectedTemplate) return null;

        return buildGroupContext({
            enrichedGroups: selectedGroupContent,
            effectiveEpr,
            templateId: selectedTemplate.templateId
        });
    }, [selectedGroupContent, effectiveEpr, selectedTemplate]);

    return { groupContext };
};

export default useGroupContext;
